import {cssInterop} from "nativewind";
import type React from "react";
import {memo} from "react";
import type {StyleProp, TextStyle, ViewStyle} from "react-native";
import type {PhoneInputProps} from "react-native-international-phone-number";
import PhoneInput from "react-native-international-phone-number";

type Props = PhoneInputProps;

type PhoneInputWithClassNamesProps = {
    inputStyle?: StyleProp<TextStyle>;
    flagStyle?: StyleProp<TextStyle>;
    containerStyle?: StyleProp<ViewStyle>;
    flagContainerStyle?: StyleProp<ViewStyle>;
    callingCodeStyle?: StyleProp<TextStyle>;
    dividerStyle?: StyleProp<ViewStyle>;
    searchContainerStyle?: StyleProp<ViewStyle>;
    searchInputStyle?: StyleProp<TextStyle>;
    countryItemStyle?: StyleProp<ViewStyle>;
    modalCallingCodeStyle?: StyleProp<TextStyle>;
    modalCountryNameStyle?: StyleProp<TextStyle>;
    caretStyle?: StyleProp<TextStyle>;
};

const PhoneInputWithClassNames = cssInterop(
    ({
        inputStyle,
        containerStyle,
        flagContainerStyle,
        flagStyle,
        callingCodeStyle,
        dividerStyle,
        searchContainerStyle,
        searchInputStyle,
        countryItemStyle,
        modalCallingCodeStyle,
        modalCountryNameStyle,
        caretStyle,
        ...props
    }: PhoneInputWithClassNamesProps & PhoneInputProps) => {
        return (
            <PhoneInput
                {...props}
                modalStyles={{
                    searchContainer: searchContainerStyle,
                    searchInput: searchInputStyle,
                    countryItem: countryItemStyle,
                    callingCode: modalCallingCodeStyle,
                    countryName: modalCountryNameStyle
                }}
                phoneInputStyles={{
                    input: inputStyle,
                    container: containerStyle,
                    flag: flagStyle,
                    callingCode: callingCodeStyle,
                    divider: dividerStyle,
                    flagContainer: flagContainerStyle,
                    caret: caretStyle
                }}
            />
        );
    },
    {
        inputClassName: "inputStyle",
        containerClassName: "containerStyle",
        flagContainerClassName: "flagContainerStyle",
        flagClassName: "flagStyle",
        callingCodeClassName: "callingCodeStyle",
        dividerClassName: "dividerStyle",
        searchContainerClassName: "searchContainerStyle",
        searchInputClassName: "searchInputStyle",
        countryItemClassName: "countryItemStyle",
        modalCallingCodeClassName: "modalCallingCodeStyle",
        modalCountryNameClassName: "modalCountryNameStyle",
        caretClassName: "caretStyle"
    }
);

const ReactNativeInternationalPhoneNumber: React.FC<Props> = props => {
    return (
        <PhoneInputWithClassNames
            {...props}
            callingCodeClassName={`
                text-blueGray-800
                font-body
                leading-sm
                text-sm
                font-normal
            `}
            caretClassName="hidden"
            className="h-[36px] web:focus-visible:outline-none"
            containerClassName={`
                !h-[36px]
                bg-white
                flex-row
                items-center
                !rounded-sm
                border
                border-solid
                flex-1
                !border-blueGray-200
            `}
            countryItemClassName={`
                border-blueGray-200
                bg-blueGray-50
            `}
            dividerClassName={`
                bg-blueGray-200
                mx-2
            `}
            flagClassName="mr-0"
            flagContainerClassName={`
                bg-blueGray-100
                px-3
                rounded-none
            `}
            inputClassName={`
                text-blueGray-800
                font-body
                leading-sm
                text-sm
                font-normal
                placeholder:text-blueGray-400
                web:focus-visible:outline-none
            `}
            modalCallingCodeClassName={`
                text-blueGray-600
                font-body
                leading-sm
                text-sm
                font-normal
            `}
            modalCountryNameClassName={`
                text-blueGray-800
                font-body
                leading-sm
                text-md
                font-normal
            `}
            searchContainerClassName={`
                !h-[46px]
                bg-white
                flex-row
                items-center
                !rounded-sm
                border
                border-solid
                pt-0
                !border-blueGray-200
                focus-within:web:ring-2
                focus-within:web:ring-blueGray-200/[.5]
            `}
            searchInputClassName={`
                text-blueGray-800
                placeholder:text-blueGray-400
                font-body
                leading-sm
                text-sm
                font-normal
                border-0
                min-h-[36px]
                web:focus-visible:outline-none
            `}
        />
    );
};

export default memo(ReactNativeInternationalPhoneNumber);
